import { createSlice } from "@reduxjs/toolkit";
import { BaseResponse, BaseResponseSingle } from "../../typings";
import { IStoreClosureDateResponse } from "../../typings/interfaces/responses/store-closure-date-response";
import { 
  getStoreClosureDates, 
  addStoreClosureDate, 
  updateStoreClosureDate, 
  deleteStoreClosureDate 
} from "../actions/storeClosureDateActions";

export interface IStoreClosureDateSlice {
  closureDates: BaseResponse<IStoreClosureDateResponse> | null;
  closureDatesLoading: boolean;
  closureDatesError: string | null;

  addClosureDateResponse: BaseResponseSingle<IStoreClosureDateResponse> | null;
  addClosureDateLoading: boolean;
  addClosureDateError: string | null;

  updateClosureDateResponse: BaseResponseSingle<IStoreClosureDateResponse> | null;
  updateClosureDateLoading: boolean;
  updateClosureDateError: string | null;

  deleteClosureDateResponse: BaseResponseSingle<boolean> | null;
  deleteClosureDateLoading: boolean;
  deleteClosureDateError: string | null;
}

const initialState: IStoreClosureDateSlice = {
  closureDates: null,
  closureDatesLoading: false,
  closureDatesError: null,

  addClosureDateResponse: null,
  addClosureDateLoading: false,
  addClosureDateError: null,

  updateClosureDateResponse: null,
  updateClosureDateLoading: false,
  updateClosureDateError: null,

  deleteClosureDateResponse: null,
  deleteClosureDateLoading: false,
  deleteClosureDateError: null,
};

const storeClosureDateSlice = createSlice({
  initialState,
  name: "storeClosureDate",
  reducers: {
    resetStoreClosureDate: (state) => {
      state.closureDates = null;
      state.closureDatesLoading = false;
      state.closureDatesError = null;

      state.addClosureDateResponse = null;
      state.addClosureDateLoading = false;
      state.addClosureDateError = null;

      state.updateClosureDateResponse = null;
      state.updateClosureDateLoading = false;
      state.updateClosureDateError = null;

      state.deleteClosureDateResponse = null;
      state.deleteClosureDateLoading = false;
      state.deleteClosureDateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get closure dates
      .addCase(getStoreClosureDates.pending, (state) => {
        state.closureDates = null;
        state.closureDatesLoading = true;
        state.closureDatesError = null;
      })
      .addCase(getStoreClosureDates.fulfilled, (state, action) => {
        state.closureDates = action.payload;
        state.closureDatesLoading = false;
        state.closureDatesError = null;
      })
      .addCase(getStoreClosureDates.rejected, (state, action) => {
        state.closureDates = null;
        state.closureDatesLoading = false;
        state.closureDatesError = (action.payload as { message?: string } | undefined)?.message ||
          action.error.message ||
          null;
      })
      // Add closure date
      .addCase(addStoreClosureDate.pending, (state) => {
        state.addClosureDateResponse = null;
        state.addClosureDateLoading = true;
        state.addClosureDateError = null;
      })
      .addCase(addStoreClosureDate.fulfilled, (state, action) => {
        state.addClosureDateResponse = action.payload;
        state.addClosureDateLoading = false;
        state.addClosureDateError = null;
      })
      .addCase(addStoreClosureDate.rejected, (state, action) => {
        state.addClosureDateResponse = null;
        state.addClosureDateLoading = false;
        state.addClosureDateError = (action.payload as { message?: string } | undefined)?.message ||
          action.error.message ||
          null;
      })
      // Update closure date
      .addCase(updateStoreClosureDate.pending, (state) => {
        state.updateClosureDateResponse = null;
        state.updateClosureDateLoading = true;
        state.updateClosureDateError = null;
      })
      .addCase(updateStoreClosureDate.fulfilled, (state, action) => {
        state.updateClosureDateResponse = action.payload;
        state.updateClosureDateLoading = false;
        state.updateClosureDateError = null;
      })
      .addCase(updateStoreClosureDate.rejected, (state, action) => {
        state.updateClosureDateResponse = null;
        state.updateClosureDateLoading = false;
        state.updateClosureDateError = (action.payload as { message?: string } | undefined)?.message ||
          action.error.message ||
          null;
      })
      // Delete closure date
      .addCase(deleteStoreClosureDate.pending, (state) => {
        state.deleteClosureDateResponse = null;
        state.deleteClosureDateLoading = true;
        state.deleteClosureDateError = null;
      })
      .addCase(deleteStoreClosureDate.fulfilled, (state, action) => {
        state.deleteClosureDateResponse = action.payload;
        state.deleteClosureDateLoading = false;
        state.deleteClosureDateError = null;
      })
      .addCase(deleteStoreClosureDate.rejected, (state, action) => {
        state.deleteClosureDateResponse = null;
        state.deleteClosureDateLoading = false;
        state.deleteClosureDateError = (action.payload as { message?: string } | undefined)?.message ||
          action.error.message ||
          null;
      });
  },
});

export default storeClosureDateSlice.reducer;
export const storeClosureDateSliceActions = storeClosureDateSlice.actions;

